import { useEffect, useState, useCallback } from 'react';

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

export function useGooglePicker(
  clientId: string | undefined,
  apiKey: string | undefined,
  onSelect: (files: File[]) => void,
  customDiscoveryDocs?: string[],
  customScopes?: string
) {
  const discoveryDocs = customDiscoveryDocs || ["https://www.googleapis.com/discovery/v1/apis/drive/v3/rest"];
  const scopes = customScopes || "https://www.googleapis.com/auth/drive.readonly";

  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [gisLoaded, setGisLoaded] = useState(false);
  const [tokenClient, setTokenClient] = useState<any>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  useEffect(() => {
    const loadScripts = () => {
      const gapiScript = document.createElement('script');
      gapiScript.src = 'https://apis.google.com/js/api.js';
      gapiScript.async = true;
      gapiScript.defer = true;
      gapiScript.onload = () => {
        if (!apiKey) return;
        window.gapi.load('client:picker', async () => {
          await window.gapi.client.init({
            apiKey: apiKey,
            discoveryDocs: discoveryDocs,
          });
          setGapiLoaded(true);
        });
      };
      document.body.appendChild(gapiScript);

      const gisScript = document.createElement('script');
      gisScript.src = 'https://accounts.google.com/gsi/client';
      gisScript.async = true;
      gisScript.defer = true;
      gisScript.onload = () => {
        if (!clientId) return;
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: scopes,
          callback: '', // defined at request time
        });
        setTokenClient(client);
        setGisLoaded(true);
      };
      document.body.appendChild(gisScript);
    };

    if (clientId && apiKey) {
      loadScripts();
    }
  }, [clientId, apiKey]);

  const createPicker = useCallback((token: string) => {
    if (!apiKey) return;
    const picker = new window.google.picker.PickerBuilder()
      .addView(window.google.picker.ViewId.DOCS)
      .setOAuthToken(token)
      .setDeveloperKey(apiKey)
      .setSize(1051, 650)
      .setCallback(async (data: any) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const files = data.docs.map((doc: any) => {
            // Since we can't easily get the actual File object from Drive without downloading,
            // we create a placeholder File object with the name and metadata.
            // In a real app, you might download the file or just store the Drive URL.
            return new File([], doc.name, { type: doc.mimeType });
          });
          onSelect(files);
        }
      })
      .build();
    picker.setVisible(true);
  }, [onSelect]);

  const openPicker = useCallback(() => {
    if (!tokenClient) return;

    // Reuse existing token if available
    if (accessToken) {
      createPicker(accessToken);
      return;
    }

    tokenClient.callback = async (response: any) => {
      if (response.error !== undefined) {
        throw response;
      }
      setAccessToken(response.access_token);
      createPicker(response.access_token);
    };

    tokenClient.requestAccessToken({ prompt: 'consent' });
  }, [tokenClient, accessToken, createPicker]);

  return { openPicker, isLoaded: gapiLoaded && gisLoaded };
}
